import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["indicator", "avatars", "count"]
  static values = { 
    checkInterval: { type: Number, default: 30000 } // 30 seconds
  }

  connect() {
    console.log('🆕 Rails 8 New Posts Controller connected')
    
    // Only check for new posts if we're on page 1
    if (this.isFirstPage()) {
      this.startChecking()
    }
  }

  disconnect() {
    this.stopChecking()
  }
  
  // Method to update lastCheckTime from external calls (like after creating a post)
  updateLastCheckTime() {
    this.lastCheckTime = new Date().toISOString()
    console.log('🕒 Updated lastCheckTime externally:', this.lastCheckTime)
  }

  isFirstPage() {
    const urlParams = new URLSearchParams(window.location.search)
    const page = urlParams.get('page')
    return !page || page === '1'
  }

  startChecking() {
    // Check immediately
    this.checkForNewPosts()
    
    // Then check periodically
    this.interval = setInterval(() => {
      this.checkForNewPosts()
    }, this.checkIntervalValue)
    
    console.log(`📅 Checking for new posts every ${this.checkIntervalValue / 1000} seconds`)
  }

  stopChecking() {
    if (this.interval) {
      clearInterval(this.interval)
      this.interval = null
    }
  }

  async checkForNewPosts() {
    try {
      // Initialize lastCheckTime if not set (on first load or refresh)
      if (!this.lastCheckTime) {
        this.lastCheckTime = new Date().toISOString()
        console.log('🕒 Initialized lastCheckTime:', this.lastCheckTime)
        return // Don't check on first load
      }
      
      const response = await fetch(`/posts/new_post_authors?since=${encodeURIComponent(this.lastCheckTime)}`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        
        if (data.count > 0) {
          this.showNewPostsIndicator(data.authors, data.count)
        }
      }
    } catch (error) {
      console.log('Error checking for new posts:', error)
    }
  }

  showNewPostsIndicator(authors, count) {
    if (!this.hasIndicatorTarget) return
    
    // Update count
    if (this.hasCountTarget) {
      this.countTarget.textContent = count
    }
    
    // Update avatars
    if (this.hasAvatarsTarget) {
      this.avatarsTarget.innerHTML = authors.map(author => {
        const displayName = this.escapeHtml(author.display_name)
        const avatarUrl = this.safeUrl(author.avatar_url)
        if (avatarUrl) {
          return `<img src="${avatarUrl}"
                       alt="${displayName}"
                       class="w-8 h-8 rounded-full border-2 border-white -ml-2 first:ml-0 object-cover"
                       title="${displayName}">`
        } else {
          // Show initials for users without avatars
          const rawInitial = author.display_initial || (author.display_name || "").charAt(0).toUpperCase()
          const initial = this.escapeHtml(rawInitial)
          return `<div class="w-8 h-8 rounded-full border-2 border-white -ml-2 first:ml-0 bg-blue-500 flex items-center justify-center text-white font-bold text-xs"
                       title="${displayName}">
                    ${initial}
                  </div>`
        }
      }).join('')
    }
    
    // Show the indicator with animation
    this.indicatorTarget.classList.remove('hidden')
    this.indicatorTarget.style.opacity = '0'
    this.indicatorTarget.style.transform = 'translateY(-10px)'
    
    requestAnimationFrame(() => {
      this.indicatorTarget.style.transition = 'all 0.3s ease-out'
      this.indicatorTarget.style.opacity = '1'
      this.indicatorTarget.style.transform = 'translateY(0)'
    })
    
    console.log(`📢 Showing ${count} new posts from:`, authors.map(a => a.display_name))
  }

  async loadNewPosts() {
    try {
      // Hide the indicator immediately for better UX
      this.indicatorTarget.classList.add('hidden')
      
      // Use our lastCheckTime instead of first post timestamp
      if (!this.lastCheckTime) {
        console.log('No lastCheckTime available, skipping load')
        return
      }
      
      // Fetch new posts since our last check
      const response = await fetch(`/posts/load_new_posts?since=${encodeURIComponent(this.lastCheckTime)}`, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (response.ok) {
        const turboStreamContent = await response.text()
        Turbo.renderStreamMessage(turboStreamContent)
        
        // Update lastCheckTime to now to prevent re-showing the same posts
        this.lastCheckTime = new Date().toISOString()
        console.log('🕒 Updated lastCheckTime after loading posts:', this.lastCheckTime)
        
        // Show a brief success message
        this.showRefreshSuccess()
        
        console.log('📈 Prepended new posts to the feed')
      } else {
        console.error('Failed to load new posts')
      }
    } catch (error) {
      console.error('Error loading new posts:', error)
    }
  }
  
  showRefreshSuccess() {
    // Create a temporary success message
    const successMsg = document.createElement('div')
    successMsg.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 transition-all duration-300'
    successMsg.innerHTML = '<i class="fas fa-check mr-2"></i>Showing latest posts'
    
    document.body.appendChild(successMsg)
    
    // Animate in
    requestAnimationFrame(() => {
      successMsg.style.opacity = '1'
      successMsg.style.transform = 'translateY(0)'
    })
    
    // Remove after 3 seconds
    setTimeout(() => {
      successMsg.style.opacity = '0'
      successMsg.style.transform = 'translateY(-20px)'
      setTimeout(() => {
        document.body.removeChild(successMsg)
      }, 300)
    }, 3000)
  }

  escapeHtml(text) {
    // Escape quotes too — these values land inside double-quoted attributes
    // (alt=, title=), where a bare " would break out of the attribute.
    return String(text == null ? "" : text).replace(/[&<>"']/g, (c) => (
      { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
    ))
  }

  // Allow only safe http(s)/relative URLs for avatar src; reject javascript: etc.
  safeUrl(url) {
    if (!url) return ""
    const raw = String(url).trim()
    if (/^(https?:\/\/|\/)/i.test(raw)) {
      return raw.replace(/"/g, "%22").replace(/</g, "%3C").replace(/>/g, "%3E")
    }
    return ""
  }
}